import { cloud } from "@app/lib/cloud";
import Alpine from "alpinejs";

type AntavoProfile = {
  id: string;
  optin_at: number;
  last_login_at: number;
  updated_at: number;
  first_name: string;
  last_name: string;
  email: string;
  birth_date?: string;
  score: number;
  tiers: Array<{
    id: string;
    name: string;
    points: 0;
    threshold: 0;
    next: {
      id: string;
      name: string;
      points: number;
    };
  }>;
};

export const store = {
  isUpdating: false,
  profile: {} as AntavoProfile,
  tiers: [] as any,
  rewards: [] as any,
  challenges: [] as any,
  claimedRewards: [] as any,
  redeemableRewards: [] as any,
  redeemedRewards: [] as any,
  history: [] as any,
  wallet: [] as any,

  createUpdateWrapper(callback: () => Promise<any>) {
    return async () => {
      this.isUpdating = true;
      await callback();
      this.isUpdating = false;
    };
  },

  async fetchCustomerProfile() {
    const handler = async () => {
      this.profile = await cloud.fetchCustomerProfile();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerTiers() {
    const handler = async () => {
      this.tiers = await cloud.fetchCustomerTiers();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerRewards() {
    const handler = async () => {
      this.rewards = await cloud.fetchCustomerRewards();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerClaimedRewards() {
    const handler = async () => {
      this.claimedRewards = await cloud.fetchCustomerClaimedRewards();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerRedeemableRewards() {
    const handler = async () => {
      this.redeemableRewards = await cloud.fetchCustomerRedeemableRewards();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerRedeemedRewards() {
    const handler = async () => {
      this.redeemedRewards = await cloud.fetchCustomerRedeemedRewards();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerChallenges() {
    const handler = async () => {
      this.challenges = await cloud.fetchCustomerChallenges();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerHistory() {
    const handler = async () => {
      this.history = await cloud.fetchCustomerHistory();
    };

    this.createUpdateWrapper(handler)();
  },

  async fetchCustomerWallet() {
    const handler = async () => {
      this.wallet = await cloud.fetchCustomerWallet();
    };

    this.createUpdateWrapper(handler)();
  },

  get currentTier() {
    if (!this.profile) return null;
    const tiers = Array.from(this.profile.tiers ?? []);
    const [tier] = tiers.reverse();
    return tier;
  },

  get accountBalance() {
    if (!this.profile) return null;
  },

  get tierProgress() {
    if (!this.profile || !this.currentTier) return null;
    const tierThreshold = this.profile.score + this.currentTier.next.points;
    const percentage = Math.min((this.profile.score / tierThreshold) * 100, 100);
    return percentage + "%";
  },

  get pointsToNextTier() {
    if (!this.profile || !this.currentTier) return null;
    return this.currentTier.next.points;
  },

  get hasNextTier() {
    if (!this.profile || !this.currentTier) return false;
    return this.currentTier.next && this.currentTier.next.points > 0;
  },

  get restrictedRewards() {
    if (!this.profile || !this.rewards) return [];
    return this.rewards.filter((reward: any) => reward.status === "restricted");
  },

  getCoupon(reward: any) {
    if (!reward?.coupons?.length) return null;
    const coupon = reward?.coupons.at(0)
    if (!coupon) {
      return {
        expiry: '',
        minimum: '',
      }
    }
    return {
      expiry: this.getCouponExpiry(coupon),
      minimum: this.getCouponMiniumSpend(coupon),
    }
  },
  getCouponExpiry(coupon: { expires_at: string }) {
    return new Date(coupon.expires_at).toLocaleDateString()
  },
  getCouponMiniumSpend(coupon: { purchase_threshold: number, cf_issued_currency: string }) {
    return `$${coupon.purchase_threshold} ${coupon.cf_issued_currency}`
  }
};

Alpine.store("loyalty", store);
